import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Bell, CheckCircle2, ShieldAlert, X, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Real notification types written by the bot (workers -> queue:notification -> db.notifications).
const TYPE_STYLE = {
  trade_executed: { badge: 'TRADE EXECUTED', icon: Zap },
  trade_closed: { badge: 'TRADE CLOSED', icon: CheckCircle2 },
  order_rejected: { badge: 'REJECTED', icon: AlertTriangle },
  emergency_exit: { badge: 'EMERGENCY EXIT', icon: ShieldAlert },
};

const SEVERITY_STYLE = {
  critical: {
    badgeColor: 'bg-red-950 text-red-300 border-red-700/50',
    iconBg: 'bg-red-900/60 text-red-300 border-red-700/50',
  },
  warning: {
    badgeColor: 'bg-amber-950 text-amber-300 border-amber-700/50',
    iconBg: 'bg-amber-900/60 text-amber-300 border-amber-700/50',
  },
  info: {
    badgeColor: 'bg-emerald-950 text-emerald-300 border-emerald-700/50',
    iconBg: 'bg-emerald-900/60 text-emerald-300 border-emerald-700/50',
  },
};

function timeAgo(ts) {
  const t = ts ? new Date(ts).getTime() : NaN;
  if (!Number.isFinite(t)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

/**
 * Latest REAL bot notification (trade executed / closed, rejection, emergency exit), polled from
 * /api/notifications. Renders nothing when there is none — it never invents trading activity.
 */
export function LiveNotifications({ pollMs = 20000 }) {
  const [latest, setLatest] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/notifications?limit=1`, { withCredentials: true });
        const n = (r?.data?.notifications || [])[0] || null;
        if (alive) setLatest(n);
      } catch {
        // logged out / backend unreachable: show nothing rather than something made up
      }
    };
    load();
    const timer = setInterval(load, pollMs);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [pollMs]);

  if (!latest || !latest.message) return null;
  const key = `${latest.timestamp || ''}|${latest.message}`;
  if (key === dismissedKey) return null;

  const type = String(latest.type || 'notification');
  const typeStyle = TYPE_STYLE[type] || {
    badge: type.replace(/_/g, ' ').toUpperCase(),
    icon: Bell,
  };
  const sev = SEVERITY_STYLE[String(latest.severity || 'info')] || SEVERITY_STYLE.info;
  const IconComp = typeStyle.icon;

  return (
    <div data-testid="live-notifications-toast" className="fixed bottom-12 left-2 right-2 sm:right-auto sm:left-5 sm:bottom-5 z-40 max-w-[380px] bg-zinc-950/95 border border-zinc-800 rounded-xl p-3 shadow-2xl backdrop-blur-md animate-in fade-in slide-in-from-bottom-3 duration-200">
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-lg border flex items-center justify-center shrink-0 ${sev.iconBg}`}>
          <IconComp className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className={`text-[10px] font-mono font-bold px-1.5 py-0.2 rounded border uppercase tracking-wider ${sev.badgeColor}`}>
                {typeStyle.badge}
              </span>
            </div>
            <span className="text-[10px] text-zinc-500 font-mono">{timeAgo(latest.timestamp)}</span>
          </div>

          <p className="text-xs font-semibold text-zinc-100 leading-snug break-words">
            {latest.message}
          </p>

          <p className="text-[9px] text-zinc-500 font-mono mt-1">
            LARA bot • {String(latest.severity || 'info').toUpperCase()}
          </p>
        </div>

        <button
          aria-label="Dismiss notification"
          onClick={() => setDismissedKey(key)}
          className="text-zinc-500 hover:text-zinc-300 p-1 rounded-md hover:bg-zinc-800 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default LiveNotifications;
